import React, { useCallback, useEffect, useMemo, useState } from "react"
import PropTypes from "prop-types"
import { useNavigate, useSearchParams } from "react-router-dom"
import dayjs from "dayjs"
import { Bounce, toast } from "react-toastify"
import DashboardCard from "../../../components/DashboardComponents/DashboardCard"
import Pagination from "../../../components/Listing/Pagination"
import SearchInput from "../../../components/Listing/SearchInput"
import FilterDropdown, {
  toSingleFilterValue,
} from "../../../components/Listing/FilterDropdown"
import ExportLogButton from "../../../components/Listing/ExportLogButton"
import CustomButton from "../../../components/OnboardingComponents/CustomButton"
import CustomIconButton from "../../../components/OnboardingComponents/CustomIconButton"
import EmptyState from "../../../components/Common/EmptyState"
import TruncatedText from "../../../components/Common/TruncatedText"
import DeleteConfirmation from "../../../components/Common/DeleteConfirmation"
import AdminCityListingTitle from "../../../components/AdminLayout/AdminCityListingTitle"
import { ICONS } from "../../../constants/icons"
import { del, get } from "../../../api/axios"
import endpoints from "../../../api/endpoints"
import { toastApiError } from "../../../helpers/apiError"
import useAdminPermission from "../../../hooks/useAdminPermission"
import {
  REWARD_STATUS_FILTER_OPTIONS,
  mapRewardRow,
  mapRewardsStatsKpi,
} from "./adminRewardsData"
import AddRewardModal from "./AddRewardModal"

const PAGE_SIZE = 10

const STATUS_LABEL = {
  active: "Active",
  draft: "Draft",
  inactive: "Inactive",
}

const TOAST_OPTIONS = {
  position: "bottom-center",
  theme: "colored",
  transition: Bounce,
}

/**
 * Shared Rewards listing — platform Admin, City Portal, and
 * Manage City Rewards (`40008029:74256`).
 */
const AdminRewards = ({
  documentTitle = "Treasure & XP | AMOK . EARTH Admin",
  detailBasePath = "/admin/rewards",
  cityId: cityIdProp,
  extraKpiCards = [],
  showKpi = true,
  showExport = true,
  showSearch = true,
  showFilter = true,
}) => {
  const navigate = useNavigate()
  const { can } = useAdminPermission()
  const canCreate = can("rewards", "create")
  const canEdit = can("rewards", "edit")
  const canDelete = can("rewards", "delete")
  const [searchParams] = useSearchParams()
  const cityId = cityIdProp || searchParams.get("city_id") || ""
  const [rows, setRows] = useState([])
  const [search, setSearch] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [statusFilter, setStatusFilter] = useState("")
  const [currentPage, setCurrentPage] = useState(1)
  const [totalItems, setTotalItems] = useState(0)
  const [pageSize, setPageSize] = useState(PAGE_SIZE)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [kpiCards, setKpiCards] = useState(() =>
    mapRewardsStatsKpi({}, extraKpiCards),
  )
  const [isKpiLoading, setIsKpiLoading] = useState(showKpi)
  const [isExporting, setIsExporting] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingRewardId, setEditingRewardId] = useState(null)
  const [rewardToDelete, setRewardToDelete] = useState(null)
  const [isDeleting, setIsDeleting] = useState(false)

  useEffect(() => {
    document.title = documentTitle
  }, [documentTitle])

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300)
    return () => clearTimeout(timer)
  }, [search])

  useEffect(() => {
    setCurrentPage(1)
  }, [debouncedSearch, statusFilter, cityId])

  const fetchStats = useCallback(async () => {
    if (!showKpi) return
    setIsKpiLoading(true)
    try {
      const params = {}
      if (cityId) params.city_id = cityId
      const response = await get(endpoints.getAdminRewardsStats, { params })
      if (response?.data?.success) {
        setKpiCards(
          mapRewardsStatsKpi(response.data.result || {}, extraKpiCards),
        )
      } else {
        toastApiError(response, "Failed to load reward stats")
      }
    } catch (err) {
      toastApiError(err, "Failed to load reward stats")
    } finally {
      setIsKpiLoading(false)
    }
  }, [cityId, extraKpiCards, showKpi])

  useEffect(() => {
    fetchStats()
  }, [fetchStats])

  const buildListParams = useCallback(
    ({ includePage = true } = {}) => {
      const params = {
        "with[]": ["creator", "medias", "cities"],
      }
      if (includePage) {
        params.page = currentPage
        params.page_size = PAGE_SIZE
      }
      if (debouncedSearch) params.search = debouncedSearch
      if (statusFilter) {
        params.status = statusFilter
      }
      if (cityId) params.city_id = cityId
      return params
    },
    [cityId, currentPage, debouncedSearch, statusFilter],
  )

  const fetchRewards = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const response = await get(endpoints.getAdminRewards, {
        params: buildListParams(),
      })
      if (response?.data?.success) {
        const result = response.data.result || {}
        const mapped = (result.data || []).map(mapRewardRow)

        setRows(mapped)
        setTotalItems(result.total ?? mapped.length)
        setPageSize(result.per_page || PAGE_SIZE)

        const totalPages = Math.max(
          1,
          Math.ceil(
            (result.total ?? mapped.length) / (result.per_page || PAGE_SIZE),
          ),
        )
        if (currentPage > totalPages) setCurrentPage(totalPages)
      } else {
        const message = toastApiError(response, "Failed to load rewards")
        setError(message)
        setRows([])
        setTotalItems(0)
      }
    } catch (err) {
      const message = toastApiError(err, "Failed to load rewards")
      setError(message)
      setRows([])
      setTotalItems(0)
    } finally {
      setIsLoading(false)
    }
  }, [buildListParams, currentPage])

  useEffect(() => {
    fetchRewards()
  }, [fetchRewards])

  const openAddModal = () => {
    setEditingRewardId(null)
    setIsModalOpen(true)
  }

  const openEditModal = row => {
    setEditingRewardId(row.id)
    setIsModalOpen(true)
  }

  const openDetail = row => {
    if (!row?.id) return
    navigate(`${detailBasePath}/${row.id}`)
  }

  const closeModal = () => {
    setIsModalOpen(false)
    setEditingRewardId(null)
  }

  const handleModalSuccess = async () => {
    await Promise.all([fetchRewards(), fetchStats()])
  }

  const closeDeleteModal = () => {
    if (isDeleting) return
    setRewardToDelete(null)
  }

  const handleDeleteConfirm = async () => {
    if (!rewardToDelete?.id || isDeleting) return

    if (!rewardToDelete.canDelete) {
      toast.error("Rewards with claims cannot be deleted.", TOAST_OPTIONS)
      setRewardToDelete(null)
      return
    }

    setIsDeleting(true)
    try {
      const response = await del(
        endpoints.deleteAdminReward(rewardToDelete.id),
        { skipErrorToast: true },
      )

      if (
        !response ||
        response.status >= 400 ||
        response?.data?.success === false
      ) {
        toastApiError(response, "Failed to delete reward")
        return
      }

      toast.success(
        response?.data?.message || `“${rewardToDelete.name}” deleted.`,
        TOAST_OPTIONS,
      )
      setRewardToDelete(null)
      await Promise.all([fetchRewards(), fetchStats()])
    } catch (err) {
      toastApiError(err, "Failed to delete reward")
    } finally {
      setIsDeleting(false)
    }
  }

  const handleExport = async () => {
    if (isExporting) return

    setIsExporting(true)
    try {
      const response = await get(endpoints.exportAdminRewards, {
        params: buildListParams({ includePage: false }),
        responseType: "blob",
        skipErrorToast: true,
      })

      if (!response || response.status >= 400) {
        toastApiError(response, "Failed to export rewards")
        return
      }

      const contentType = String(
        response.headers?.["content-type"] || "",
      ).toLowerCase()

      if (contentType.includes("application/json")) {
        const text = await response.data.text()
        let message = "Failed to export rewards"
        try {
          const parsed = JSON.parse(text)
          message = parsed?.message || message
        } catch {
          // keep default message
        }
        toast.error(message, TOAST_OPTIONS)
        return
      }

      const disposition = response.headers?.["content-disposition"] || ""
      const filenameMatch = disposition.match(
        /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/i,
      )
      const filename = filenameMatch?.[1]
        ? filenameMatch[1].replace(/['"]/g, "")
        : `rewards-${dayjs().format("YYYY-MM-DD")}.csv`

      const blob =
        response.data instanceof Blob
          ? response.data
          : new Blob([response.data], { type: "text/csv;charset=utf-8;" })
      const url = URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(url)

      toast.success("Rewards exported.", TOAST_OPTIONS)
    } catch (err) {
      toastApiError(err, "Failed to export rewards")
    } finally {
      setIsExporting(false)
    }
  }

  const skeletonRows = useMemo(
    () => Array.from({ length: 6 }, (_, index) => `skeleton-${index}`),
    [],
  )

  const safePage = Math.min(
    currentPage,
    Math.max(1, Math.ceil(totalItems / pageSize) || 1),
  )

  return (
    <div
      className={`admin-page admin-rewards${
        !showKpi ? " admin-rewards--city-manage" : ""
      }`}
    >
      {showKpi ? (
        <div
          className={`admin-rewards__kpi-grid${
            kpiCards.length >= 4 ? " admin-rewards__kpi-grid--four" : ""
          }`}
        >
          {kpiCards.map(card => (
            <DashboardCard
              key={card.key}
              title={card.title}
              tone={card.tone}
              className="admin-rewards__kpi-card"
              fullHeight
            >
              <p className="admin-rewards__kpi-value">
                {isKpiLoading ? (
                  <span className="admin-rewards__skeleton admin-rewards__skeleton--kpi" />
                ) : (
                  card.value
                )}
              </p>
            </DashboardCard>
          ))}
        </div>
      ) : null}

      <div className="admin-rewards__toolbar">
        {cityId ? (
          <AdminCityListingTitle label="Treasure" cityId={cityId} />
        ) : (
          <h1 className="admin-rewards__title">Treasure</h1>
        )}
        <div className="admin-rewards__toolbar-actions">
          {showSearch ? (
            <SearchInput
              value={search}
              onChange={event => setSearch(event.target.value)}
              placeholder="Search by Treasure..."
              iconSrc={ICONS.callFormalSearchStatus}
              className="admin-rewards__search"
            />
          ) : null}
          {showFilter ? (
            <FilterDropdown
              label="Filter"
              filters={[
                {
                  key: "status",
                  type: "select",
                  label: "Status",
                  options: REWARD_STATUS_FILTER_OPTIONS,
                  value: statusFilter,
                  defaultValue: "",
                  onChange: next => {
                    setStatusFilter(toSingleFilterValue(next))
                  },
                },
              ]}
            />
          ) : null}
          {showExport ? (
            <ExportLogButton
              entityLabel="rewards"
              onClick={handleExport}
              loading={isExporting}
            />
          ) : null}
          {canCreate ? (
            <CustomButton
              variant="primary"
              size="md"
              type="button"
              trailingIconSrc={ICONS.plus}
              trailingIconAlt=""
              onClick={openAddModal}
            >
              Add Treasure
            </CustomButton>
          ) : null}
        </div>
      </div>

      <div className="admin-rewards__table-wrapper">
        <div
          className="admin-rewards__table"
          role="table"
          aria-busy={isLoading || undefined}
        >
          <div
            className="admin-rewards__table-row admin-rewards__table-row--head"
            role="row"
          >
            <div role="columnheader">Treasure Name</div>
            <div role="columnheader">Type</div>
            <div role="columnheader">Cities</div>
            <div role="columnheader">Description</div>
            <div role="columnheader">Claims</div>
            <div role="columnheader">Quantity Left</div>
            <div role="columnheader">Status</div>
            <div role="columnheader" className="admin-rewards__actions-head">
              Actions
            </div>
          </div>

          {isLoading &&
            skeletonRows.map(key => (
              <div
                key={key}
                className="admin-rewards__table-row admin-rewards__table-row--skeleton"
                role="row"
              >
                <div className="admin-rewards__skeleton" />
                <div className="admin-rewards__skeleton" />
                <div className="admin-rewards__skeleton" />
                <div className="admin-rewards__skeleton" />
                <div className="admin-rewards__skeleton" />
                <div className="admin-rewards__skeleton" />
                <div className="admin-rewards__skeleton" />
                <div className="admin-rewards__skeleton" />
              </div>
            ))}

          {!isLoading &&
            rows.map(row => (
              <div key={row.id} className="admin-rewards__table-row" role="row">
                <div
                  className="admin-rewards__cell admin-rewards__cell--name"
                  role="cell"
                >
                  <div className="admin-rewards__name-wrap">
                    <span
                      className="admin-rewards__thumb admin-rewards__thumb--initials"
                      aria-hidden="true"
                    >
                      {row.initials}
                    </span>
                    <TruncatedText className="admin-rewards__name">
                      {row.name}
                    </TruncatedText>
                  </div>
                </div>
                <TruncatedText
                  as="div"
                  className="admin-rewards__cell"
                  role="cell"
                >
                  {row.typeLabel}
                </TruncatedText>
                <TruncatedText
                  as="div"
                  className="admin-rewards__cell"
                  role="cell"
                  title={row.citiesLabel || undefined}
                >
                  {row.citiesLabel || "--"}
                </TruncatedText>
                <TruncatedText
                  as="div"
                  className="admin-rewards__cell"
                  role="cell"
                  title={row.description || undefined}
                >
                  {row.description || "--"}
                </TruncatedText>
                <TruncatedText
                  as="div"
                  className="admin-rewards__cell"
                  role="cell"
                >
                  {row.claimsCount.toLocaleString("en-US")}
                </TruncatedText>
                <TruncatedText
                  as="div"
                  className="admin-rewards__cell"
                  role="cell"
                >
                  {row.quantityLabel}
                </TruncatedText>
                <div className="admin-rewards__status-cell" role="cell">
                  <span
                    className={`admin-rewards__status admin-rewards__status--${row.status}`}
                  >
                    <span
                      className="admin-rewards__status-dot"
                      aria-hidden="true"
                    />
                    {STATUS_LABEL[row.status] || row.status}
                  </span>
                </div>
                <div className="admin-rewards__actions" role="cell">
                  <CustomButton
                    variant="transparent"
                    size="sm"
                    type="button"
                    className="admin-rewards__view-btn"
                    trailingIconSrc={ICONS.arrowRight12}
                    trailingIconAlt=""
                    onClick={() => openDetail(row)}
                  >
                    View Details
                  </CustomButton>
                  {canEdit ? (
                    <CustomIconButton
                      iconSrc={ICONS.edit}
                      label={`Edit ${row.name}`}
                      size="sm"
                      onClick={() => openEditModal(row)}
                    />
                  ) : null}
                  {canDelete ? (
                    <CustomIconButton
                      iconSrc={ICONS.trash}
                      label={
                        row.canDelete
                          ? `Delete ${row.name}`
                          : `Delete ${row.name} (has claims)`
                      }
                      tone="danger"
                      size="sm"
                      disabled={!row.canDelete}
                      onClick={() => {
                        if (!row.canDelete) return
                        setRewardToDelete(row)
                      }}
                    />
                  ) : null}
                </div>
              </div>
            ))}

          {!isLoading && !error && rows.length === 0 && (
            <div className="admin-rewards__empty">
              <EmptyState
                title="No treasures found"
                description="Add a treasure to get started."
              />
            </div>
          )}

          {!isLoading && error && rows.length === 0 && (
            <div className="admin-rewards__empty">
              <EmptyState
                title="Unable to load rewards"
                description={error}
                actionText="Try again"
                onActionClick={fetchRewards}
              />
            </div>
          )}
        </div>
      </div>

      <Pagination
        currentPage={safePage}
        totalItems={totalItems}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
        itemLabel="Treasure"
        prevIconSrc={ICONS.chevronLeft}
        nextIconSrc={ICONS.chevronRight}
        className="admin-rewards__pagination"
      />

      <AddRewardModal
        isOpen={isModalOpen}
        onClose={closeModal}
        onSuccess={handleModalSuccess}
        rewardId={editingRewardId}
      />

      <DeleteConfirmation
        isOpen={Boolean(rewardToDelete)}
        onCancel={closeDeleteModal}
        onConfirm={handleDeleteConfirm}
        title="Delete Reward"
        message={
          rewardToDelete
            ? `Are you sure you want to delete “${rewardToDelete.name}”? This action cannot be undone.`
            : ""
        }
        confirmText="Delete Reward"
        confirmLoadingText="Deleting..."
        isDeleting={isDeleting}
      />
    </div>
  )
}

AdminRewards.propTypes = {
  documentTitle: PropTypes.string,
  detailBasePath: PropTypes.string,
  cityId: PropTypes.string,
  extraKpiCards: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      value: PropTypes.string,
      tone: PropTypes.string,
    }),
  ),
  showKpi: PropTypes.bool,
  showExport: PropTypes.bool,
  showSearch: PropTypes.bool,
  showFilter: PropTypes.bool,
}

AdminRewards.defaultProps = {
  documentTitle: "Treasure & XP | AMOK . EARTH Admin",
  detailBasePath: "/admin/rewards",
  cityId: undefined,
  extraKpiCards: [],
  showKpi: true,
  showExport: true,
  showSearch: true,
  showFilter: true,
}

export { CITY_PORTAL_REWARD_KPI, REWARD_KPI } from "./adminRewardsData"
export default AdminRewards
